"use client";

import { Button } from "@shared/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@shared/components/ui/card";
import { Input } from "@shared/components/ui/input";
import { Label } from "@shared/components/ui/label";
import { Skeleton } from "@shared/components/ui/skeleton";
import { IconDice, IconLoader2 } from "@tabler/icons-react";
import { gql } from "graphql-request";
import { useRouter } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { PageHeader } from "@/components/page-header";
import { useAuth } from "@/lib/auth";
import { formatUserError } from "@/lib/errors";
import { useGraphqlMutation, useGraphqlQuery } from "@/lib/graphql/useGraphql";
import { orgRoleCanManageOrgSettings } from "@/lib/org/org-constants";
import { slugifyOrgName } from "@/lib/org/slugify";
import { toastUserError } from "@/lib/toast-user-error";
import { useDebouncedValue } from "@/lib/use-debounced-value";
import { cn } from "@/lib/utils";

const orgPageDocument = gql`
  query ManageOrgPage {
    me {
      org {
        id
        name
        kind
        slug
        slug_user_set
        is_platform_superuser_org
      }
    }
  }
`;

const updateOrgProfileDocument = gql`
  mutation UpdateOrgProfile($id: ID!, $name: String!, $slug: String!, $slugUserSet: Boolean!) {
    updateOrg(id: $id, name: $name, slug: $slug, slug_user_set: $slugUserSet) {
      id
      name
      slug
      slug_user_set
    }
  }
`;

const orgSlugAvailableDocument = gql`
  query OrgSlugAvailable($slug: String!, $excludeOrgId: ID!) {
    orgSlugAvailable(slug: $slug, exclude_org_id: $excludeOrgId)
  }
`;

const randomOrgSlugDocument = gql`
  query RandomOrgSlug($excludeOrgId: ID!) {
    randomOrgSlug(exclude_org_id: $excludeOrgId)
  }
`;

type OrgPageQuery = {
  me: {
    org: {
      id: string;
      name: string;
      kind: string;
      slug: string | null;
      slug_user_set: boolean;
      is_platform_superuser_org: boolean;
    } | null;
  };
};

type SlugAvailabilityStatus = "idle" | "checking" | "available" | "unavailable" | "error";

const readOnlyFieldClassName =
  "bg-muted/40 pointer-events-none cursor-default focus-visible:border-input focus-visible:ring-0";

export default function OrganizationPage() {
  const router = useRouter();
  const { orgRole } = useAuth();
  const canManageOrgSettings = orgRoleCanManageOrgSettings(orgRole);
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [checkSlug, setCheckSlug] = useState("");
  const nameInputRef = useRef<HTMLInputElement>(null);
  const availabilityErrorToasted = useRef<string | null>(null);
  const debouncedSlug = useDebouncedValue(slug, 400);

  const query = useGraphqlQuery<OrgPageQuery>({
    queryKey: ["manageOrgPage"],
    document: orgPageDocument,
  });
  const updateProfile = useGraphqlMutation<
    {
      updateOrg: {
        id: string;
        name: string;
        slug: string | null;
        slug_user_set: boolean;
      };
    },
    { id: string; name: string; slug: string; slugUserSet: boolean }
  >({ document: updateOrgProfileDocument });
  const randomizeSlug = useGraphqlMutation<{ randomOrgSlug: string }, { excludeOrgId: string }>({
    document: randomOrgSlugDocument,
  });

  const org = query.data?.me.org ?? null;
  const isPlatformSuperuserOrg = org?.is_platform_superuser_org === true;

  useEffect(() => {
    if (org?.kind === "personal") {
      router.replace("/manage/members");
    }
  }, [org?.kind, router]);

  const trimmedName = name.trim();
  const trimmedSlug = slug.trim().toLowerCase();
  const trimmedCheck = checkSlug.trim().toLowerCase();
  const orgSlug = (org?.slug ?? "").trim().toLowerCase();
  const slugAutoSync = org !== null && !org.slug_user_set && !slugTouched;
  const nameDirty = org !== null && trimmedName !== org.name.trim();
  const slugDirty = org !== null && trimmedSlug !== orgSlug;
  const isDirty = nameDirty || slugDirty;
  const slugNeedsCheck = slugDirty && trimmedSlug !== "";
  const isCheckSynced = trimmedCheck === trimmedSlug;
  const shouldCheckAvailability = isEditing && org !== null && slugNeedsCheck && isCheckSynced;

  const availabilityQuery = useGraphqlQuery<{ orgSlugAvailable: boolean }, { slug: string; excludeOrgId: string }>({
    queryKey: ["orgSlugAvailable", org?.id ?? null, trimmedCheck],
    document: orgSlugAvailableDocument,
    variables: org && shouldCheckAvailability ? { slug: trimmedCheck, excludeOrgId: org.id } : undefined,
    enabled: Boolean(org) && shouldCheckAvailability,
    retry: false,
  });

  let availabilityStatus: SlugAvailabilityStatus = "idle";
  if (isEditing && slugNeedsCheck) {
    if (!isCheckSynced || availabilityQuery.isFetching) {
      availabilityStatus = "checking";
    } else if (availabilityQuery.isError) {
      availabilityStatus = "error";
    } else if (availabilityQuery.isSuccess) {
      availabilityStatus = availabilityQuery.data.orgSlugAvailable ? "available" : "unavailable";
    } else {
      availabilityStatus = "checking";
    }
  }

  const slugReady =
    !slugNeedsCheck || availabilityStatus === "available" || (trimmedSlug !== "" && trimmedSlug === orgSlug);
  const canSave =
    org !== null &&
    isEditing &&
    isDirty &&
    trimmedName !== "" &&
    trimmedSlug !== "" &&
    slugReady &&
    availabilityStatus !== "checking" &&
    availabilityStatus !== "unavailable" &&
    availabilityStatus !== "error" &&
    !updateProfile.isPending;
  const showSpinner = availabilityStatus === "checking" || updateProfile.isPending;
  const primaryLabel =
    availabilityStatus === "unavailable"
      ? "Slug unavailable"
      : availabilityStatus === "error"
        ? "Can't verify"
        : "Save";

  useEffect(() => {
    if (org && !isEditing) {
      setName(org.name);
      setSlug(org.slug ?? "");
      setCheckSlug(org.slug ?? "");
      setSlugTouched(false);
    }
  }, [org, isEditing]);

  useEffect(() => {
    if (!isEditing) {
      return;
    }
    setCheckSlug(debouncedSlug);
  }, [debouncedSlug, isEditing]);

  useEffect(() => {
    if (!isEditing) {
      return;
    }
    nameInputRef.current?.focus();
    nameInputRef.current?.select();
  }, [isEditing]);

  useEffect(() => {
    if (availabilityStatus !== "error" || !availabilityQuery.error) {
      availabilityErrorToasted.current = null;
      return;
    }
    const message = formatUserError(availabilityQuery.error, "Could not verify organization slug");
    if (availabilityErrorToasted.current === message) {
      return;
    }
    availabilityErrorToasted.current = message;
    toast.error(message);
  }, [availabilityStatus, availabilityQuery.error]);

  const resetDraft = () => {
    if (!org) {
      return;
    }
    setName(org.name);
    setSlug(org.slug ?? "");
    setCheckSlug(org.slug ?? "");
    setSlugTouched(false);
  };

  const handleStartEdit = () => {
    if (isPlatformSuperuserOrg) {
      return;
    }
    resetDraft();
    setIsEditing(true);
  };

  const handleCancel = () => {
    resetDraft();
    setIsEditing(false);
  };

  const handleNameChange = (value: string) => {
    setName(value);
    if (slugAutoSync) {
      setSlug(slugifyOrgName(value));
    }
  };

  const handleSlugChange = (value: string) => {
    setSlugTouched(true);
    setSlug(slugifyOrgName(value));
  };

  const handleRandomizeSlug = async () => {
    if (!org || !isEditing || randomizeSlug.isPending) {
      return;
    }
    try {
      const data = await randomizeSlug.mutateAsync({ excludeOrgId: org.id });
      const next = slugifyOrgName(data.randomOrgSlug);
      setSlugTouched(true);
      setSlug(next);
      setCheckSlug(next);
    } catch (error) {
      toastUserError(error, "Failed to generate a slug");
    }
  };

  const handleSave = async () => {
    if (!org || !canSave) {
      return;
    }
    const lockSlug = org.slug_user_set || slugTouched;
    try {
      await updateProfile.mutateAsync({
        id: org.id,
        name: trimmedName,
        slug: trimmedSlug,
        slugUserSet: lockSlug,
      });
      toast.success("Organization updated");
      setIsEditing(false);
      setSlugTouched(false);
      await query.refetch();
    } catch (error) {
      toastUserError(error, "Failed to update organization");
    }
  };

  const handlePrimaryClick = () => {
    if (availabilityStatus === "error") {
      void availabilityQuery.refetch();
      return;
    }
    void handleSave();
  };

  return (
    <div className="@container/main flex flex-1 flex-col gap-2">
      <div className="flex-1 space-y-6">
        <PageHeader title="Organization" description="Manage your organization." />

        {query.isPending || org?.kind === "personal" ? (
          <Skeleton className="h-40 w-full" />
        ) : !org ? (
          <p className="text-muted-foreground text-sm">No organization found for this account.</p>
        ) : (
          <div className="grid gap-4">
            <Card>
              <CardHeader>
                <CardTitle>Details</CardTitle>
                <CardDescription>Type: Team</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex max-w-lg flex-col gap-4">
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="org-name">Organization name</Label>
                    <Input
                      ref={nameInputRef}
                      id="org-name"
                      className={cn(!isEditing && readOnlyFieldClassName)}
                      value={name}
                      readOnly={!isEditing}
                      onChange={(event) => handleNameChange(event.target.value)}
                      placeholder="Acme"
                      onKeyDown={(event) => {
                        if (!isEditing) {
                          return;
                        }
                        if (event.key === "Escape") {
                          event.preventDefault();
                          handleCancel();
                        }
                      }}
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    <Label htmlFor="org-slug">Slug</Label>
                    <div className="flex gap-2">
                      <Input
                        id="org-slug"
                        className={cn("min-w-0 flex-1 font-mono text-sm", !isEditing && readOnlyFieldClassName)}
                        value={slug}
                        readOnly={!isEditing}
                        aria-invalid={availabilityStatus === "unavailable" || undefined}
                        onChange={(event) => handleSlugChange(event.target.value)}
                        onBlur={() => {
                          if (isEditing) {
                            setCheckSlug(slug);
                          }
                        }}
                        placeholder="acme-robotics"
                        onKeyDown={(event) => {
                          if (!isEditing) {
                            return;
                          }
                          if (event.key === "Escape") {
                            event.preventDefault();
                            handleCancel();
                            return;
                          }
                          if (event.key === "Enter") {
                            event.preventDefault();
                            setCheckSlug(slug);
                            if (canSave) {
                              void handleSave();
                            }
                          }
                        }}
                      />
                      {isEditing ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => void handleRandomizeSlug()}
                          disabled={randomizeSlug.isPending || updateProfile.isPending}
                          aria-label="Generate random slug"
                          title="Generate random slug"
                        >
                          {randomizeSlug.isPending ? (
                            <IconLoader2 className="size-4 animate-spin" aria-hidden />
                          ) : (
                            <IconDice className="size-4" aria-hidden />
                          )}
                        </Button>
                      ) : null}
                    </div>
                    {isEditing && slugAutoSync ? (
                      <p className="text-muted-foreground text-xs">
                        Slug updates from the name until you edit it directly.
                      </p>
                    ) : null}
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {isPlatformSuperuserOrg ? (
                      <p className="text-muted-foreground text-sm">
                        This is the platform superuser organization. Name, slug, and org type cannot be changed.
                      </p>
                    ) : !canManageOrgSettings ? null : isEditing ? (
                      <>
                        <Button
                          type="button"
                          onClick={handlePrimaryClick}
                          disabled={
                            showSpinner || (!canSave && availabilityStatus !== "error") || updateProfile.isPending
                          }
                          aria-busy={showSpinner}
                          className="relative transition-[width,min-width,padding] duration-200 ease-out"
                        >
                          <span className={cn("inline-flex items-center justify-center", showSpinner && "invisible")}>
                            {primaryLabel}
                          </span>
                          {showSpinner ? (
                            <span className="absolute inset-0 flex items-center justify-center">
                              <IconLoader2 className="size-4 animate-spin" aria-hidden />
                              <span className="sr-only">
                                {updateProfile.isPending ? "Saving organization" : "Checking slug availability"}
                              </span>
                            </span>
                          ) : null}
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleCancel}
                          disabled={updateProfile.isPending}
                        >
                          Cancel
                        </Button>
                      </>
                    ) : (
                      <Button type="button" variant="outline" onClick={handleStartEdit}>
                        Edit
                      </Button>
                    )}
                  </div>
                </div>

                {isPlatformSuperuserOrg ? null : (
                  <p className="text-muted-foreground text-sm">
                    This is a team organization. Invite members from the Members page.
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
